import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, PawPrint, Minus, Plus, Search, ChevronRight, Phone } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { createPet, getCustomers } from '../services/api';
import { toast } from 'sonner';

const AddPet = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [customerId, setCustomerId] = useState(location.state?.customerId || '');
  const [customerName, setCustomerName] = useState(location.state?.customerName || '');
  const [loading, setLoading] = useState(false);

  // Customer search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const [formData, setFormData] = useState({
    name: '', species: 'dog', breed: '', gender: '', color: '', age: 0, notes: '',
  });

  const searchCustomers = async (q) => {
    setSearchQuery(q);
    if (q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await getCustomers(q);
      setSearchResults(res.data);
    } catch (e) { console.error(e); }
    finally { setSearching(false); }
  };

  const selectCustomer = (c) => {
    setCustomerId(c.id);
    setCustomerName(c.name);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!customerId) { toast.error(`Önce müşteri seçin`); return; }
    setLoading(true);
    try {
      await createPet({ ...formData, age: formData.age || null, customer_id: customerId });
      toast.success('Hasta eklendi');
      navigate(-1);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Bir hata oluştu`);
    } finally { setLoading(false); }
  };

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  return (
    <div className="min-h-screen bg-[#F2F2F7]">
      <header className="bg-white px-4 pt-12 pb-4 shadow-sm sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-gray-100" data-testid="back-btn">
            <ArrowLeft size={24} className="text-gray-900" />
          </button>
          <h1 className="text-xl font-bold text-gray-900 font-['Plus_Jakarta_Sans']">Yeni Hasta Ekle</h1>
        </div>
      </header>

      <form onSubmit={handleSubmit} className="p-4 space-y-4">
        {/* Customer Selection */}
        {!customerId ? (
          <div className="ios-card space-y-3">
            <h2 className="text-sm font-semibold text-[#8E8E93] uppercase tracking-wider">{`Müşteri Seç`}</h2>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" size={18} />
              <Input
                type="text"
                placeholder={`Müşteri Ara (İsim, telefon veya TCKN)`}
                value={searchQuery}
                onChange={(e) => searchCustomers(e.target.value)}
                className="pl-10 h-12 bg-[#F2F2F7] border-0 rounded-xl"
                autoFocus
                data-testid="customer-search-input"
              />
            </div>
            {searchResults.length > 0 && (
              <div className="divide-y divide-gray-100 rounded-xl overflow-hidden border border-gray-100">
                {searchResults.slice(0, 5).map((c) => (
                  <button key={c.id} type="button" onClick={() => selectCustomer(c)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 text-left" data-testid={`search-customer-${c.id}`}>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{c.name}</p>
                      <p className="text-xs text-[#8E8E93] flex items-center gap-1"><Phone size={10} /> {c.phone}</p>
                    </div>
                    <ChevronRight size={16} className="text-[#C7C7CC]" />
                  </button>
                ))}
              </div>
            )}
            {searchQuery.length >= 2 && searchResults.length === 0 && !searching && (
              <p className="text-xs text-center text-[#8E8E93]">{`Müşteri bulunamadı`}</p>
            )}
          </div>
        ) : (
          <div className="ios-card flex items-center justify-between">
            <div>
              <p className="text-xs text-[#8E8E93]">{`Müşteri`}</p>
              <p className="font-semibold text-gray-900">{customerName}</p>
            </div>
            <Button type="button" variant="ghost" size="sm" onClick={() => { setCustomerId(''); setCustomerName(''); }} className="text-[#6b6de3] text-sm rounded-full">
              {`Değiştir`}
            </Button>
          </div>
        )}

        {/* Pet Info */}
        <div className="ios-card space-y-4">
          <h2 className="text-sm font-semibold text-[#8E8E93] uppercase tracking-wider">Hasta Bilgileri</h2>
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">{`Ad *`}</Label>
            <div className="relative">
              <PawPrint className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" size={20} />
              <Input type="text" placeholder={`Hasta adı`} value={formData.name} onChange={(e) => handleChange('name', e.target.value)} className="pl-10 h-12 text-base bg-[#F2F2F7] border-0 rounded-xl" required data-testid="pet-name-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">{`Tür *`}</Label>
            <Select value={formData.species} onValueChange={(v) => handleChange('species', v)}>
              <SelectTrigger className="h-12 text-base bg-[#F2F2F7] border-0 rounded-xl" data-testid="pet-species-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="dog">{`Köpek`}</SelectItem>
                <SelectItem value="cat">Kedi</SelectItem>
                <SelectItem value="bird">{`Kuş`}</SelectItem>
                <SelectItem value="other">{`Diğer`}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">{`Irk`}</Label>
            <Input type="text" placeholder={`Örn: Golden Retriever`} value={formData.breed} onChange={(e) => handleChange('breed', e.target.value)} className="h-12 text-base bg-[#F2F2F7] border-0 rounded-xl" data-testid="pet-breed-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Cinsiyet</Label>
              <Select value={formData.gender} onValueChange={(v) => handleChange('gender', v)}>
                <SelectTrigger className="h-12 text-base bg-[#F2F2F7] border-0 rounded-xl"><SelectValue placeholder={`Seçin`} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Erkek</SelectItem>
                  <SelectItem value="female">{`Dişi`}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Renk</Label>
              <Input type="text" placeholder={`Örn: Sarı`} value={formData.color} onChange={(e) => handleChange('color', e.target.value)} className="h-12 text-base bg-[#F2F2F7] border-0 rounded-xl" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">{`Yaş`}</Label>
            <div className="flex items-center gap-4">
              <button type="button" onClick={() => handleChange('age', Math.max(0, formData.age - 1))} className="w-12 h-12 rounded-xl bg-[#F2F2F7] flex items-center justify-center text-gray-700 hover:bg-gray-200 transition-colors" data-testid="pet-age-minus"><Minus size={20} /></button>
              <div className="flex-1 text-center">
                <span className="text-2xl font-bold text-gray-900" data-testid="pet-age-value">{formData.age || 0}</span>
                <p className="text-xs text-[#8E8E93]">{`yaşında`}</p>
              </div>
              <button type="button" onClick={() => handleChange('age', Math.min(25, formData.age + 1))} className="w-12 h-12 rounded-xl bg-[#F2F2F7] flex items-center justify-center text-gray-700 hover:bg-gray-200 transition-colors" data-testid="pet-age-plus"><Plus size={20} /></button>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">Notlar</Label>
            <Textarea placeholder={`Özel notlar...`} value={formData.notes} onChange={(e) => handleChange('notes', e.target.value)} className="text-base bg-[#F2F2F7] border-0 rounded-xl min-h-[80px]" />
          </div>
        </div>

        <Button type="submit" disabled={loading || !formData.name || !customerId} className="w-full h-14 text-base font-semibold bg-[#6b6de3] hover:bg-[#5658c4] rounded-full" data-testid="add-pet-submit-btn">
          {loading ? 'Kaydediliyor...' : 'Hasta Ekle'}
        </Button>
      </form>
    </div>
  );
};

export default AddPet;
